import classes from './VBar.module.scss'
import classNames from 'classnames'
import { Tokens } from '@grammarly/design-system'
import { v4 as uuid } from 'uuid'
import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { truncateText } from './truncateText'
import { createAnimationFrameScheduler } from './createAnimationFrameScheduler'
import {
  defaultVBarFontMetrics,
  type VBarFontMetrics,
  VBarFontMetricsContext,
} from './VBarFontMetricsContext'

export interface VBarProps extends VBarFontMetrics {
  readonly color?: string
  readonly strokeColor?: string
  readonly fillColor?: string
  readonly isActive?: boolean
  readonly primaryText?: string
  readonly primaryTextColor?: string
  readonly secondaryText?: string
  readonly secondaryTextColor?: string
  readonly ariaControls?: string
  readonly ariaExpanded?: boolean
  readonly anchorHeight?: number
  readonly maxTextWidth?: number
  /**
   * The number of lines to show for the primary text (if allowed by available space).
   */
  readonly primaryTextLines?: number
  readonly onClick?: () => void
  readonly onHover?: (hovered: boolean) => void
}

/**
 * A shared component for rendering our various vbars on the left-hand side of
 * the content
 */
export const VBar = memo(
  ({
    primaryText,
    secondaryText,
    color = Tokens.Color.Blue40,
    strokeColor = color,
    fillColor = color,
    primaryTextColor = color,
    secondaryTextColor,
    isActive = false,
    ariaControls,
    ariaExpanded,
    primaryTextLines = 2,
    maxTextWidth = 150,
    anchorHeight,
    lineHeight,
    fontSize,
    fontFamily,
    fontWeight,
    fontStyle,
    fontVariant,
    onClick,
    onHover,
  }: VBarProps) => {
    const primaryTextId = useMemo(() => uuid(), [])
    const vbarRef = useRef<HTMLButtonElement>(null)
    const textRef = useRef<HTMLDivElement>(null)
    const canvasCtx = useMemo<CanvasRenderingContext2D | null>(() => {
      const canvas = document.createElement('canvas')
      return canvas.getContext('2d')
    }, [])
    const [truncatedText, setTruncatedText] = useState({ primary: '', secondary: '' })

    const contextFontMetrics = useContext(VBarFontMetricsContext)

    // We set all the text styles from JS so we don't have to run `getComputedStyle` to access them
    // as this creates major performance issues with lots of VBars.
    const fontMetrics = useMemo(
      () => ({
        lineHeight:
          lineHeight ?? contextFontMetrics.lineHeight ?? defaultVBarFontMetrics.lineHeight,
        fontSize: fontSize ?? contextFontMetrics.fontSize ?? defaultVBarFontMetrics.fontSize,
        fontFamily:
          fontFamily ?? contextFontMetrics.fontFamily ?? defaultVBarFontMetrics.fontFamily,
        fontWeight:
          fontWeight ?? contextFontMetrics.fontWeight ?? defaultVBarFontMetrics.fontWeight,
        fontStyle: fontStyle ?? contextFontMetrics.fontStyle ?? defaultVBarFontMetrics.fontStyle,
        fontVariant:
          fontVariant ?? contextFontMetrics.fontVariant ?? defaultVBarFontMetrics.fontVariant,
      }),
      [lineHeight, fontSize, fontFamily, fontWeight, fontStyle, fontVariant, contextFontMetrics],
    )

    useEffect(() => {
      const vbarContainer = vbarRef.current
      const textContainer = textRef.current
      if (!vbarContainer || !textContainer || !canvasCtx) return

      const truncate = (containerWidth: number, containerHeight: number) => {
        // Canvas font format: [font-style] [font-variant] [font-weight] [font-size]/[line-height] [font-family]
        const fontString = `${fontMetrics.fontStyle} ${fontMetrics.fontVariant} ${fontMetrics.fontWeight} ${fontMetrics.fontSize}px/${fontMetrics.lineHeight}px ${fontMetrics.fontFamily}`
        canvasCtx.font = fontString

        const maxWidth = Math.min(containerWidth, maxTextWidth)
        const totalLines = Math.floor(containerHeight / fontMetrics.lineHeight)

        const primaryTextTrimmed = (primaryText ?? '').trim()
        const secondaryTextTrimmed = (secondaryText ?? '').trim()

        let lineCounts = { primary: totalLines, secondary: 0 }
        if (secondaryTextTrimmed) {
          const minSecondary = 1
          const maxPrimary = Math.max(1, Math.min(primaryTextLines, totalLines - minSecondary))
          const remaining = Math.max(totalLines - maxPrimary, minSecondary)
          lineCounts = {
            primary: Math.min(maxPrimary, totalLines),
            secondary: Math.min(remaining, totalLines),
          }
        }

        const primary = truncateText(canvasCtx, primaryTextTrimmed, maxWidth, lineCounts.primary)
        const secondary = truncateText(
          canvasCtx,
          secondaryTextTrimmed,
          maxWidth,
          lineCounts.secondary,
        )

        setTruncatedText({ primary, secondary })
      }

      const disconnect = observeSizeChangesInAnimationFrame(vbarContainer, entries =>
        truncate(
          textContainer.clientWidth,
          // just trying to minimize reading sizes from the DOM to reduce layout thrashing
          entries.length > 0 ? entries[0].contentRect.height : vbarContainer.clientHeight,
        ),
      )

      return disconnect
    }, [primaryText, secondaryText, fontMetrics, canvasCtx, primaryTextLines, maxTextWidth])

    return (
      <button
        ref={vbarRef}
        className={classNames(classes.root, { [classes.active]: isActive })}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        aria-labelledby={primaryTextId}
        onClick={onClick}
        onPointerEnter={() => onHover?.(true)}
        onPointerLeave={() => onHover?.(false)}
        style={{ '--vbar-max-width': `${maxTextWidth}` } as unknown as React.CSSProperties}
      >
        <div
          ref={textRef}
          className={classes.text}
          style={{
            fontFamily: fontMetrics.fontFamily,
            fontSize: `${fontMetrics.fontSize}px`,
            lineHeight: `${fontMetrics.lineHeight}px`,
            fontWeight: fontMetrics.fontWeight,
            fontStyle: fontMetrics.fontStyle,
            fontVariant: fontMetrics.fontVariant,
          }}
        >
          {truncatedText.primary ? (
            <span id={primaryTextId} style={{ color: primaryTextColor }}>
              {truncatedText.primary}
            </span>
          ) : null}
          {truncatedText.secondary ? (
            <span
              id={truncatedText.primary ? undefined : primaryTextId}
              className={classes.secondaryText}
              style={{ color: secondaryTextColor }}
            >
              {truncatedText.secondary}
            </span>
          ) : null}
        </div>
        <div className={classes.bar} style={{ height: anchorHeight }}>
          <div
            className={classes.dot}
            style={{ backgroundColor: fillColor, borderColor: strokeColor }}
          />
          <div className={classes.line} />
        </div>
      </button>
    )
  },
)

/**
 * Observes size changes of an element using ResizeObserver in a requestAnimationFrame loop.
 * The callback is also called immediately (in next raf) with an empty array.
 * Calls are deduped through the scheduler.
 */
function observeSizeChangesInAnimationFrame(
  element: HTMLElement,
  callback: (entries: ResizeObserverEntry[]) => void,
): VoidFunction {
  const { schedule, cancel } = createAnimationFrameScheduler()

  const observer = new ResizeObserver(entries => schedule(callback, entries))
  observer.observe(element)

  // Initial call with empty entries
  schedule(callback, [])

  return () => {
    observer.disconnect()
    cancel()
  }
}
